// #region Imports
import BadgeIcon from "@mui/icons-material/Badge";
import MessageIcon from "@mui/icons-material/Message";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import Loader from "_shared/_views/loading/loader";
import Guid from "_shared/guids/guid";
import CompanyAvatar from "marketplace/_views/avatars/company-avatar";
import {
  getCompanyById,
  getErrorLoadingCompanyById,
  getIsLoadingCompanyById,
  populateCompanyById
} from "marketplace/companies/_store/companies-redux-slice";
import CompanyProfile from "marketplace/companies/profiles/company-profile";
import ChatWindow from "messaging/forums/_views/components/forum-chat-window";
import Forum from "messaging/forums/forum";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useSession } from "users/__session/session-context";
import { selectIsNetworkedWith } from "users/networking/connections/_selectors/user-network-connection-selectors";
import NetworkButton from "users/networking/connections/_views/components/network-button";
// #endregion

// #region Styled Components
const MainContainer = styled("div")(() => ({
  display: "grid",
  gridTemplateRows: "auto 1fr",
  width: "100%",
}));
const HeaderContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("lg")]: {
    padding: theme.spacing(3, 0),
  },
  alignItems: "center",
  backgroundColor: theme.palette.background.paper,
  display: "flex",
  flexDirection: "column",
  gridColumn: "1 / auto",
  gridRowStart: "1",
  justifyContent: "center",
  padding: theme.spacing(3),
  paddingTop: theme.spacing(1),
  position: "sticky",
  top: 0,
  width: "100%",
  zIndex: 10,
}));
const ChatContainer = styled(Container)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    padding: 0,
  },
  [theme.breakpoints.up("md")]: {
    padding: theme.spacing(0, 1),
  },
  [theme.breakpoints.up("lg")]: {
    padding: theme.spacing(0, 4),
  },
  gridColumn: "1 / auto",
  gridRowStart: "2",
  gridRowEnd: "none",
}));
const ContentContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.up("md")]: {
    margin: theme.spacing(0, 1),
  },
  [theme.breakpoints.up("lg")]: {
    margin: theme.spacing(0, 4),
  },
  columnFill: "balance",
  columnGap: theme.spacing(1),
  gridColumn: "1 / auto",
  gridRowStart: "2",
}));
const HeaderInnerContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    justifyItems: "center",
  },
  [theme.breakpoints.up("sm")]: {
    gridTemplateColumns: "min-content auto",
    gridTemplateRows: "min-content auto",
  },
  [theme.breakpoints.up("lg")]: {
    gridTemplateColumns: "min-content auto 1fr",
  },
  alignItems: "flex-start",
  gap: theme.spacing(2),
  display: "grid",
  width: "100%",
}));
const TitleContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
  },
  display: "flex",
  flexDirection: "column",
  height: "100%",
}));
const ButtonContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "flex-start",
  marginTop: theme.spacing(1),
  "& > *:not(:last-child)": {
    marginRight: theme.spacing(2),
  },
}));
const GridContainer = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
  width: "100%",
}));
const StyledAvatar = styled(CompanyAvatar)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    height: theme.spacing(7),
    width: theme.spacing(7),
  },
  height: theme.spacing(15),
  width: theme.spacing(15),
}));
const FieldTitle = styled(Typography)(() => ({
  fontWeight: 500,
}));
const ProfileTypeChip = styled(Chip)(({ theme }) => ({
  color: theme.palette.text.disabled,
  margin: theme.spacing(0.25),
}));
const NameChipContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
  },
  alignItems: "flex-start",
  display: "flex",
  flex: 1,
  flexDirection: "column",
  justifyContent: "flex-start",
}));
const DescriptionContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    alignItems: "center",
    justifyContent: "center",
    margin: theme.spacing(1, 0),
    width: "100%",
  },
  [theme.breakpoints.up("sm")]: {
    gridColumn: "1 / span 2",
    gridRow: "2",
    margin: theme.spacing(2, 0),
  },
  [theme.breakpoints.up("lg")]: {
    gridColumn: "3",
    gridRow: "1",
    margin: 0,
  },
  display: "flex",
  flexDirection: "column",
  height: "100%",
  justifyContent: "space-between",
  margin: 0,
  textAlign: "left",
  width: "100%",
}));
const DescriptionText = styled(Typography)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    textAlign: "center",
  },
  fontSize: "1.1em",
  fontStyle: "italic",
  lineHeight: "1.1em",
}));
const DescriptionDivider = styled(Divider)(({ theme }) => ({
  marginTop: theme.spacing(1),
  width: "100%",
}));
const MissingField = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.disabled,
}));
const MessageButtonContainer = styled("span")(() => ({
  cursor: "pointer",
}));
// #endregion

// #region Component Input Props
export type ViewCompanyProfileProps = {
  profile?: CompanyProfile;
  companyId: Guid;
};

export default function ViewCompanyProfile(props: Readonly<ViewCompanyProfileProps>) {
  const { companyId } = props;

  // #region State
  const [forum] = useState<Forum | undefined>(undefined);
  const [companyProfile, setCompanyProfile] = useState<CompanyProfile | undefined>(props.profile);
  const [chatOpen, setChatOpen] = useState<boolean>(false);
  const [viewingOwnProfile, setViewingOwnProfile] = useState<boolean>(false);

  // #region Hooks
  const session = useSession();
  const dispatch = useAttorneyHubDispatch();

  // #region Selectors
  const company = useSelector(getCompanyById(companyId));
  const isLoadingCompany = useSelector(getIsLoadingCompanyById(companyId));
  const errorLoadingCompany = useSelector(getErrorLoadingCompanyById(companyId));
  const isNetworkedWithCompany = useSelector(selectIsNetworkedWith(company?.contactId));

  // #region Lifecycle Hooks
    useEffect(() => {
    if (company || isLoadingCompany || errorLoadingCompany) return;
    dispatch(populateCompanyById({ session, companyId }));
  }, [
    company,
    isLoadingCompany,
    errorLoadingCompany,
    session,
    companyId,
  ]);

  useEffect(() => {
    if (!company?.profile) return;
    if (session.user?.id?.isEqualTo(company.contactId)) {
      setViewingOwnProfile(true);
    }
    setCompanyProfile(company.profile);
  }, [
    company,
    session
  ]);

  useEffect(() => {
    if (errorLoadingCompany) {
      enqueueSnackbar("Couldn't load company profile.", { variant: "warning" });
    }
  }, [errorLoadingCompany]);
  // #endregion

  // #region Render
  return (
    <MainContainer>
      <HeaderContainer>
        <HeaderInnerContainer>
          <StyledAvatar 
            company={company}
            session={session}
          />
          <TitleContainer>
            <NameChipContainer>
              <Typography variant="h4">{companyProfile?.name}</Typography>
              <span>
                {companyProfile?.categories?.map((category) => (
                  <ProfileTypeChip key={category} label={category} size="small" />
                ))}
              </span>
            </NameChipContainer>
            {companyProfile?.contactUserId && (
              <ButtonContainer>
                <NetworkButton userId={companyProfile?.contactUserId} />
                <Tooltip
                  title={(function () {
                    if (chatOpen) return "Back to profile";
                    if (companyProfile.contactUserId && isNetworkedWithCompany)
                      return "Send message";
                    if (viewingOwnProfile) return "You can't send a message to your own company";
                    return "You must be in the company's network to send a message";
                  })()}
                >
                  <MessageButtonContainer>
                    <Button
                      variant="contained"
                      startIcon={chatOpen ? <BadgeIcon /> : <MessageIcon />}
                      disableElevation
                      disabled={
                        viewingOwnProfile ||
                        !isNetworkedWithCompany
                      }
                      color="primary"
                      onClick={() => setChatOpen((prevValue) => !prevValue)}
                    >
                      {chatOpen ? "Back to profile" : "Message"}
                    </Button>
                  </MessageButtonContainer>
                </Tooltip>
              </ButtonContainer>
            )}
          </TitleContainer>
          <DescriptionContainer>
            <DescriptionText variant="subtitle1">{companyProfile?.description}</DescriptionText>
            <DescriptionDivider />
          </DescriptionContainer>
        </HeaderInnerContainer>
      </HeaderContainer>
      <ChatContainer>
        {isLoadingCompany && <Loader />}
        {chatOpen && forum && <ChatWindow forums={[forum]} />}
      </ChatContainer>
      <ContentContainer>
        {!chatOpen && !isLoadingCompany && (
          <>
            {companyProfile?.yearEstablished ? (
              <GridContainer>
                <FieldTitle>Year Established</FieldTitle>
                <Typography>{companyProfile?.yearEstablished?.toString()}</Typography>
              </GridContainer>
            ) : (
              <MissingField />
            )}
            {companyProfile?.numberOfEmployees ? (
              <GridContainer>
                <FieldTitle>Number of Employees</FieldTitle>
                <Typography>{companyProfile?.numberOfEmployees?.toString()}</Typography>
              </GridContainer>
            ) : (
              <MissingField />
            )}
            {companyProfile?.website ? (
              <GridContainer>
                <FieldTitle>Website</FieldTitle>
                <Link href={companyProfile?.website?.toString()} rel="noopener">
                  {companyProfile?.website?.toString()}
                </Link>
              </GridContainer>
            ) : (
              <MissingField />
            )}
            {companyProfile?.location ? (
              <GridContainer>
                <FieldTitle>Location</FieldTitle>
                <Typography>{companyProfile?.location?.toString()}</Typography>
              </GridContainer>
            ) : (
              <MissingField />
            )}
          </>
        )}
      </ContentContainer>
    </MainContainer>
  );
}
// #endregion
